import {
  analyticsAuditorQueryKeys,
  analyticsCaeQueryKeys,
  analyticsCfoQueryKeys,
  analyticsControllerQueryKeys,
  analyticsReviewerQueryKeys,
  analyticsScopeQueryKeys,
  analyticsTesterQueryKeys,
} from '../../queries/analytics/keys.ts';
import type { CFOComplianceScoreResponse, CFOOpenIssuesResponse } from '../../request/analytics/cfoReports.ts';
import type {
  ScopeControlCountsResponse,
  ScopeEntitiesResponse,
  ScopeMaterialityResponse,
  ScopeOverdueSummaryResponse,
  ScopePlannedCompletedResponse,
  ScopeRiskSummaryResponse,
  ScopeSystemsResponse,
  ScopeTestingStatusRollupResponse,
} from '../../request/analytics/scopeReports.ts';
import type { TesterAvgTestTimeResponse } from '../../request/analytics/testerReports.ts';
import type { ReviewerQueueResponse, ReviewerSummaryResponse } from '../../request/analytics/reviewerReports.ts';
import type { AuditorProcessChartingStatusResponse } from '../../request/analytics/auditorReports.ts';

/**
 * Rotating, randomly-generated sample data for the self-serve dashboard's
 * widget-gallery *previews* only (`WidgetPreview` in `SelfServeDashboard.tsx`).
 * Every real dashboard / placed-widget render keeps fetching real backend
 * data through the normal hooks in `src/hooks/analytics/*` — nothing here is
 * consumed outside the gallery preview path.
 *
 * Each `WIDGET_KEY` below maps to a generator that returns data shaped
 * EXACTLY like the corresponding `fetch*` response in
 * `src/request/analytics/{controller,cae,cfo,tester,auditor}Reports.ts`
 * (same field names/types the widget's own React Query hook resolves to),
 * so the widget component renders it with zero changes. Generators are
 * called fresh each time (see `generatePreviewData`), so re-opening the
 * gallery — or re-mounting a preview — rotates the sample values.
 */

// ---------------------------------------------------------------------------
// Randomization utilities
// ---------------------------------------------------------------------------

function randInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function randFloat(min: number, max: number, decimals = 1): number {
  const v = Math.random() * (max - min) + min;
  return Number(v.toFixed(decimals));
}

function pick<T>(arr: readonly T[]): T {
  return arr[randInt(0, arr.length - 1)];
}

function pickMany<T>(arr: readonly T[], count: number): T[] {
  const pool = [...arr];
  const out: T[] = [];
  for (let i = 0; i < count && pool.length > 0; i++) {
    out.push(pool.splice(randInt(0, pool.length - 1), 1)[0]);
  }
  return out;
}

function chance(p: number): boolean {
  return Math.random() < p;
}

function maybeNull<T>(value: T, p = 0.8): T | null {
  return chance(p) ? value : null;
}

/** Builds a random-length array (inclusive bounds) — previews stay small on purpose. */
function makeRows<T>(min: number, max: number, factory: (i: number) => T): T[] {
  const n = randInt(min, max);
  return Array.from({ length: n }, (_, i) => factory(i));
}

function shortId(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 8)}`;
}

/** ISO date-only string offset from today by a random number of days in [minDays, maxDays]. */
function isoDate(minDays: number, maxDays: number): string {
  const d = new Date();
  d.setDate(d.getDate() + randInt(minDays, maxDays));
  return d.toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
// Shared sample pools
// ---------------------------------------------------------------------------

const PEOPLE = [
  'Alicia Chen',
  'Marcus Webb',
  'Priya Sharma',
  'Diego Fernandez',
  'Hannah Lee',
  'Omar Haddad',
  'Grace Kim',
  'Nadia Petrov',
  'Sam Okafor',
  'Jordan Blake',
];

const DEPARTMENTS = ['Finance', 'IT', 'Procurement', 'HR', 'Revenue', 'Treasury', 'Supply Chain', 'Legal'];

const CONTROL_TITLES = [
  'Journal Entry Review',
  'Access Provisioning Review',
  'Bank Reconciliation',
  'Revenue Cutoff Testing',
  'Vendor Master Data Change Review',
  'Segregation of Duties - AP',
  'Payroll Change Approval',
  'System Change Management',
  'Inventory Valuation Review',
  'Fixed Asset Capitalization Review',
  'User Access Recertification',
  'Month-End Close Checklist Review',
];

const RISK_DESCRIPTIONS = [
  'Unauthorized journal entries could misstate financial results',
  'Excess access rights could allow unauthorized transactions',
  'Revenue could be recognized in the wrong period',
  'Vendor master changes could enable fraudulent payments',
  'Manual payroll overrides could bypass approval controls',
  'Inventory could be misstated due to untimely valuation review',
];

const ISSUE_TITLES = [
  'Late completion of quarterly bank reconciliation',
  'Segregation of duties conflict in AP module',
  'Unremediated access review exception',
  'Missing evidence for Q2 control execution',
  'Revenue cutoff testing exception',
  'Untimely payroll change approval',
];

const ITGC_DOMAINS = [
  { code: 'ITGC-AC', name: 'Access Controls' },
  { code: 'ITGC-CM', name: 'Change Management' },
  { code: 'ITGC-OPS', name: 'IT Operations' },
  { code: 'ITGC-DEV', name: 'Program Development' },
];

const SYSTEMS = [
  { system_code: 'ERP-01', name: 'Core ERP' },
  { system_code: 'PAY-02', name: 'Payroll System' },
  { system_code: 'CRM-03', name: 'CRM Platform' },
  { system_code: 'TRE-04', name: 'Treasury Workstation' },
];

const ATTRIBUTE_LABELS = [
  'Approval evidenced',
  'Amount agrees to supporting documentation',
  'Timeliness of review',
  'Segregation of duties maintained',
  'Completeness of population',
];

const FINDINGS = [
  'No exceptions noted during testing.',
  'Evidence of review was not retained for one sample.',
  'Approval occurred after the transaction date.',
  'Supporting documentation matched the general ledger.',
  'Sample selected outside the population reconciliation.',
];

const OBSERVATIONS = [
  'Approval signature missing on one of five samples.',
  'Documentation retained matched expectations.',
  'Amounts tied out to supporting sub-ledger detail.',
  'Review occurred two days after the control deadline.',
  'No deviations identified in the sample tested.',
];

const RERUN_REASONS = [
  'Insufficient evidence',
  'Sample failed initial testing',
  'Population reconciliation issue',
  'Reviewer requested additional support',
];

const WORKFLOW_STATUSES = ['not_started', 'in_progress', 'pending_review', 'completed'];
const OVERALL_STATUSES = ['effective', 'ineffective', 'not_tested'];
const CONCLUSIONS = ['pass', 'fail', 'not_concluded'];
const SEVERITIES = ['low', 'medium', 'high', 'critical'];
const CLASSIFICATIONS = ['control_deficiency', 'significant_deficiency', 'material_weakness'];
const FREQUENCIES = ['annual', 'quarterly', 'monthly', 'event-driven'];
const CONTROL_TYPES = ['manual', 'automated', 'it-dependent'];
const SIGNIFICANCE = ['key', 'non-key'];
const SAMPLING_METHODS = ['random', 'judgmental', 'systematic'];
const ISSUE_STATUSES = ['open', 'in_remediation', 'closed', 'escalated'];
const ACTION_STATUSES = ['not_started', 'in_progress', 'completed', 'overdue'];
const RISK_LEVELS = ['low', 'medium', 'high'];
const RISK_CLASSIFICATIONS = ['financial', 'operational', 'compliance', 'strategic', 'it'];
const CYCLES = ['Revenue', 'Procure-to-Pay', 'Order-to-Cash', 'Financial Close', 'Payroll'];
const PERIODS = ['Q1 2026', 'Q2 2026', 'Q3 2026', 'FY2025'];

function randomControlId(): string {
  return shortId('ctl');
}

function randomTitle(): string {
  return pick(CONTROL_TITLES);
}

// ---------------------------------------------------------------------------
// Controller (reports #20-#28) — src/request/analytics/controllerReports.ts
// ---------------------------------------------------------------------------

const genControllerTestSummary = () =>
  makeRows(3, 5, () => ({
    control_id: randomControlId(),
    title: randomTitle(),
    workflow_status: pick(WORKFLOW_STATUSES),
    overall_status: maybeNull(pick(OVERALL_STATUSES)),
    conclusion: maybeNull(pick(CONCLUSIONS)),
    attributes_passed: randInt(0, 8),
    attributes_failed: randInt(0, 3),
    total_exceptions: randInt(0, 5),
    confidence_score: maybeNull(randFloat(0.55, 0.99, 2)),
    testing_period_start: isoDate(-90, -30),
    testing_period_end: isoDate(-29, 0),
  }));

const genControllerFailingControls = () =>
  makeRows(3, 4, () => ({
    control_id: randomControlId(),
    title: randomTitle(),
    department: maybeNull(pick(DEPARTMENTS)),
    attributes_failed: randInt(1, 4),
    total_exceptions: randInt(1, 6),
    confidence_score: maybeNull(randFloat(0.3, 0.7, 2)),
  }));

const genControllerExceptionRate = () =>
  makeRows(4, 6, () => {
    const pop = randInt(80, 5000);
    return {
      control_id: randomControlId(),
      title: randomTitle(),
      pop,
      sample: Math.max(5, Math.round(pop * randFloat(0.02, 0.15, 3))),
      avg_deviation: randFloat(0, 12, 1),
      total_exceptions: randInt(0, 8),
    };
  });

const genControllerSampleSize = () =>
  makeRows(3, 5, () => {
    const population = randInt(100, 8000);
    return {
      control_id: randomControlId(),
      title: randomTitle(),
      population,
      sample_size: Math.max(5, Math.round(population * randFloat(0.01, 0.1, 3))),
      sampling_method: maybeNull(pick(SAMPLING_METHODS)),
      deviation_rate: randFloat(0, 10, 1),
    };
  });

const genControllerRollforward = () =>
  makeRows(3, 5, () => ({
    control_id: randomControlId(),
    title: randomTitle(),
    run_number: randInt(1, 3),
    is_rollforward: chance(0.4),
    rerun_reason: maybeNull(pick(RERUN_REASONS), 0.5),
    workflow_status: pick(WORKFLOW_STATUSES),
    created_at: isoDate(-45, -1),
  }));

const genControllerIPEStatus = () =>
  makeRows(4, 6, () => ({
    control_id: randomControlId(),
    title: randomTitle(),
    ipe_validated: maybeNull(chance(0.7)),
    workflow_status: maybeNull(pick(WORKFLOW_STATUSES)),
  }));

const genControllerToDToE = () =>
  makeRows(3, 5, () => ({
    control_id: randomControlId(),
    title: randomTitle(),
    test_type: maybeNull(pick(['ToD', 'ToE'])),
    overall_status: maybeNull(pick(OVERALL_STATUSES)),
    conclusion: maybeNull(pick(CONCLUSIONS)),
    run_count: randInt(1, 4),
  }));

const genControllerSoDVerified = () =>
  makeRows(3, 5, () => {
    const [preparer, reviewer] = pickMany(PEOPLE, 2);
    return {
      control_id: randomControlId(),
      title: randomTitle(),
      segregation_verified: maybeNull(chance(0.75)),
      preparer_name: maybeNull(preparer),
      reviewer_name: maybeNull(reviewer),
    };
  });

const genControllerEvidenceCoverage = () =>
  makeRows(4, 6, () => {
    const samples = randInt(5, 60);
    const covered = randInt(0, samples);
    return {
      control_id: randomControlId(),
      samples,
      covered,
      coverage_pct: Number(((covered / samples) * 100).toFixed(1)),
    };
  });

// ---------------------------------------------------------------------------
// CAE / Manager (reports #9-#19) — src/request/analytics/caeReports.ts
// ---------------------------------------------------------------------------

const genCaeControlUniverse = () =>
  makeRows(4, 6, () => ({
    control_id: randomControlId(),
    title: randomTitle(),
    control_type: pick(CONTROL_TYPES),
    frequency: maybeNull(pick(FREQUENCIES)),
    status: pick(['active', 'inactive']),
    significance: maybeNull(pick(SIGNIFICANCE)),
    department: maybeNull(pick(DEPARTMENTS)),
    classification: maybeNull(pick(CLASSIFICATIONS)),
    mrc_control: chance(0.25),
    external_auditor_reliance: chance(0.35),
    itgc_domain: maybeNull(pick(ITGC_DOMAINS).code, 0.4),
  }));

const genCaeTestingStatus = () =>
  makeRows(4, 6, () => ({
    control_id: randomControlId(),
    title: randomTitle(),
    workflow_status: maybeNull(pick(WORKFLOW_STATUSES)),
    assignee_user_id: maybeNull(randInt(100, 999)),
    assignee_name: maybeNull(pick(PEOPLE)),
    due_date: maybeNull(isoDate(-5, 30)),
    overall_status: maybeNull(pick(OVERALL_STATUSES)),
    conclusion: maybeNull(pick(CONCLUSIONS)),
    attributes_passed: maybeNull(randInt(0, 8)),
    attributes_failed: maybeNull(randInt(0, 3)),
    testing_period_start: isoDate(-90, -30),
    testing_period_end: isoDate(-29, 0),
  }));

const genCaeIssuesPipeline = () =>
  makeRows(3, 5, () => {
    const total = randInt(1, 6);
    return {
      id: shortId('iss'),
      title: pick(ISSUE_TITLES),
      status: pick(ISSUE_STATUSES),
      classification: maybeNull(pick(CLASSIFICATIONS)),
      department: maybeNull(pick(DEPARTMENTS)),
      total_actions: total,
      completed_actions: randInt(0, total),
      earliest_due: maybeNull(isoDate(-10, 20)),
    };
  });

const genCaeCoverageMatrix = () =>
  makeRows(3, 5, () => ({
    risk_id: maybeNull(shortId('risk')),
    risk_description: maybeNull(pick(RISK_DESCRIPTIONS)),
    risk_level: maybeNull(pick(RISK_LEVELS)),
    mapped_controls: randInt(0, 6),
  }));

const genCaeMRC = () =>
  makeRows(3, 5, () => ({
    control_id: randomControlId(),
    title: randomTitle(),
    frequency: maybeNull(pick(FREQUENCIES)),
    status: pick(['active', 'inactive']),
    overall_status: maybeNull(pick(OVERALL_STATUSES)),
    conclusion: maybeNull(pick(CONCLUSIONS)),
  }));

const genCaeExceptionsSummary = () =>
  makeRows(4, 6, () => ({
    control_id: randomControlId(),
    title: randomTitle(),
    exceptions: maybeNull(randInt(0, 10)),
    avg_deviation: maybeNull(randFloat(0, 12, 1)),
    failed_runs: randInt(0, 4),
  }));

const genCaeAuditorReliance = () =>
  makeRows(3, 5, () => ({
    control_id: randomControlId(),
    title: randomTitle(),
    significance: maybeNull(pick(SIGNIFICANCE)),
    status: pick(['active', 'inactive']),
    overall_status: maybeNull(pick(OVERALL_STATUSES)),
  }));

const genCaeActionPlanAging = () =>
  makeRows(3, 5, () => ({
    id: shortId('act'),
    issue_title: pick(ISSUE_TITLES),
    action_title: `Remediate: ${pick(ISSUE_TITLES).toLowerCase()}`,
    status: pick(ACTION_STATUSES),
    owner_name: maybeNull(pick(PEOPLE)),
    due_date: maybeNull(isoDate(-40, 10)),
    days_overdue: maybeNull(randInt(0, 90)),
  }));

const genCaeControlsByDept = () =>
  pickMany(DEPARTMENTS, randInt(3, 5)).map((dept) => {
    const total = randInt(5, 40);
    const active = randInt(0, total);
    return {
      dept,
      control_type: pick(CONTROL_TYPES),
      total,
      active,
      key_ctrl: randInt(0, active),
    };
  });

const genCaeITGCCoverage = () =>
  ITGC_DOMAINS.map(({ code, name }) => {
    const controls = randInt(3, 20);
    const active = randInt(0, controls);
    return {
      code,
      name,
      controls,
      active,
      passed: randInt(0, active),
    };
  });

const genCaeTesterWorkload = () =>
  pickMany(PEOPLE, randInt(3, 5)).map((tester) => {
    const assigned = randInt(3, 25);
    return {
      user_id: randInt(100, 999),
      tester,
      assigned,
      completed: randInt(0, assigned),
      next_due: maybeNull(isoDate(-2, 21)),
    };
  });

// ---------------------------------------------------------------------------
// CFO / Executive (reports #1-#8) — src/request/analytics/cfoReports.ts
// ---------------------------------------------------------------------------

const genCfoComplianceScore = (): CFOComplianceScoreResponse => {
  const score = randInt(55, 98);
  return {
    row: {
      score,
      coverage_ratio: randFloat(0.6, 1, 2),
      test_pass_ratio: randFloat(0.6, 1, 2),
      issue_penalty: randFloat(0, 0.35, 2),
      band: score >= 85 ? 'Healthy' : score >= 65 ? 'Watch' : 'Critical',
      refreshed_at: new Date().toISOString(),
    },
    not_computed: false,
  };
};

const genCfoCoverageHeatmap = () =>
  pickMany(CYCLES, randInt(3, 5)).map((cycle) => {
    const controls = randInt(10, 60);
    return {
      cycle,
      controls,
      active_controls: randInt(0, controls),
    };
  });

const genCfoOpenIssues = (): CFOOpenIssuesResponse => ({
  summary: {
    open_count: randInt(2, 25),
    escalated_count: randInt(0, 6),
    repeat_count: randInt(0, 8),
    overdue_action_plans: randInt(0, 10),
  },
  by_department: pickMany(DEPARTMENTS, randInt(3, 5)).map((department) => ({
    department,
    classification: pick(CLASSIFICATIONS),
    count: randInt(1, 12),
  })),
});

const genCfoProgramProgress = () =>
  WORKFLOW_STATUSES.map((result) => ({
    result,
    controls: randInt(0, 40),
  }));

const genCfoSOCCoverage = () =>
  SYSTEMS.map(({ system_code, name }) => ({
    system_code,
    name,
    key_system: chance(0.6),
    year: maybeNull(2024 + randInt(0, 2)),
    soc1_type1: maybeNull(chance(0.5)),
    soc1_type2: maybeNull(chance(0.5)),
    soc2_type1: maybeNull(chance(0.5)),
    soc2_type2: maybeNull(chance(0.5)),
    bridge_letter: maybeNull(chance(0.5)),
  }));

const genCfoRiskLandscape = () =>
  makeRows(4, 6, () => ({
    classification: pick(RISK_CLASSIFICATIONS),
    risk_level: maybeNull(pick(RISK_LEVELS)),
    cnt: randInt(1, 25),
  }));

const genCfoRepeatEscalated = () =>
  makeRows(3, 5, () => ({
    id: shortId('iss'),
    title: pick(ISSUE_TITLES),
    status: pick(ISSUE_STATUSES),
    vintage_count: randInt(1, 4),
    is_escalated: chance(0.4),
    department: maybeNull(pick(DEPARTMENTS)),
    period: maybeNull(pick(PERIODS)),
  }));

const genCfoScoreTrend = () => {
  const months = randInt(6, 8);
  let score = randInt(60, 95);
  const out: { snapshot_date: string; score: number; band: string }[] = [];
  for (let i = months; i >= 0; i--) {
    score = Math.min(99, Math.max(45, score + randInt(-6, 6)));
    const d = new Date();
    d.setMonth(d.getMonth() - i);
    out.push({
      snapshot_date: d.toISOString().slice(0, 10),
      score,
      band: score >= 85 ? 'Healthy' : score >= 65 ? 'Watch' : 'Critical',
    });
  }
  return out;
};

// ---------------------------------------------------------------------------
// Tester "my work" (reports #30-#36) — src/request/analytics/testerReports.ts
// ---------------------------------------------------------------------------

const genTesterMyControls = () =>
  makeRows(3, 5, () => ({
    control_id: randomControlId(),
    title: randomTitle(),
    frequency: maybeNull(pick(FREQUENCIES)),
    workflow_status: pick(WORKFLOW_STATUSES),
    due_date: maybeNull(isoDate(-3, 21)),
    testing_period_start: isoDate(-90, -30),
    testing_period_end: isoDate(-29, 0),
  }));

const genTesterDueThisWeek = () =>
  makeRows(2, 5, () => ({
    control_id: randomControlId(),
    title: randomTitle(),
    due_date: maybeNull(isoDate(0, 6)),
    workflow_status: pick(WORKFLOW_STATUSES),
  }));

const genTesterMyProgress = () =>
  WORKFLOW_STATUSES.map((workflow_status) => {
    const controls = randInt(0, 12);
    return {
      workflow_status,
      controls,
      passed: maybeNull(randInt(0, controls)),
      failed: maybeNull(randInt(0, controls)),
    };
  });

const genTesterMyExceptions = () =>
  makeRows(3, 5, () => ({
    control_id: randomControlId(),
    label: maybeNull(pick(ATTRIBUTE_LABELS)),
    status: maybeNull(pick(CONCLUSIONS)),
    deviation_rate: maybeNull(randFloat(0, 15, 1)),
    findings_summary: maybeNull(pick(FINDINGS)),
    record_ref: shortId('REC'),
    observation: maybeNull(pick(OBSERVATIONS)),
    severity: maybeNull(pick(SEVERITIES)),
  }));

const genTesterRerunsRequired = () =>
  makeRows(2, 4, () => ({
    control_id: randomControlId(),
    title: randomTitle(),
    rerun_reason: maybeNull(pick(RERUN_REASONS)),
    workflow_status: pick(WORKFLOW_STATUSES),
    created_at: isoDate(-30, -1),
  }));

// ---------------------------------------------------------------------------
// External Auditor (reports #37-#41) — src/request/analytics/auditorReports.ts
// ---------------------------------------------------------------------------

const genAuditorControlInventory = () =>
  makeRows(4, 6, () => ({
    control_id: randomControlId(),
    title: randomTitle(),
    control_type: maybeNull(pick(CONTROL_TYPES)),
    frequency: maybeNull(pick(FREQUENCIES)),
    significance: maybeNull(pick(SIGNIFICANCE)),
    mrc_control: chance(0.25),
    status: maybeNull(pick(['active', 'inactive'])),
  }));

const genAuditorExceptionReport = () =>
  makeRows(3, 5, () => ({
    control_id: randomControlId(),
    title: randomTitle(),
    attribute_ref: maybeNull(shortId('ATTR')),
    label: maybeNull(pick(ATTRIBUTE_LABELS)),
    record_ref: shortId('REC'),
    observation: maybeNull(pick(OBSERVATIONS)),
    severity: maybeNull(pick(SEVERITIES)),
    deviation_rate: maybeNull(randFloat(0, 15, 1)),
  }));

const genAuditorIssueLedger = () =>
  makeRows(3, 5, () => {
    const total = randInt(1, 6);
    return {
      title: pick(ISSUE_TITLES),
      status: pick(ISSUE_STATUSES),
      classification: maybeNull(pick(CLASSIFICATIONS)),
      department: maybeNull(pick(DEPARTMENTS)),
      period: maybeNull(pick(PERIODS)),
      is_repeat: chance(0.3),
      vintage_count: randInt(1, 4),
      summary_of_deficiency: maybeNull(pick(FINDINGS)),
      linked_controls: maybeNull(pickMany(CONTROL_TITLES, randInt(1, 2)).join(', ')),
      resolved_actions: randInt(0, total),
      total_actions: total,
    };
  });

const genAuditorRelianceEligible = () =>
  makeRows(3, 5, () => ({
    control_id: randomControlId(),
    title: randomTitle(),
    control_type: maybeNull(pick(CONTROL_TYPES)),
    significance: maybeNull(pick(SIGNIFICANCE)),
    mrc_control: chance(0.3),
    itgc_domain: maybeNull(pick(ITGC_DOMAINS).code, 0.4),
    max_risk_level: maybeNull(pick(RISK_LEVELS)),
  }));

// ---------------------------------------------------------------------------
// Scope & Coverage, Reviewer, Tester avg-time, Auditor process charting
// (feedback round 2026-08) — src/request/analytics/{scope,reviewer}Reports.ts
// ---------------------------------------------------------------------------

const RISK_LEVEL_ORDER = ['high', 'medium', 'low', 'unrated'];

const genScopeControlCounts = (): ScopeControlCountsResponse => {
  const keyCount = randInt(20, 60);
  const nonKeyCount = randInt(10, 40);
  const prevTotal = maybeNull(randInt(15, 90), 0.7);
  const total = keyCount + nonKeyCount;
  return {
    total,
    key_count: keyCount,
    non_key_count: nonKeyCount,
    by_risk_level: RISK_LEVEL_ORDER.map((level) => ({ level, count: randInt(0, 20) })),
    prev_total: prevTotal,
    delta: prevTotal != null ? total - prevTotal : null,
  };
};

const genScopeRiskSummary = (): ScopeRiskSummaryResponse => {
  const mapped = randInt(5, 15);
  const unmapped = randInt(0, 6);
  const total = mapped + unmapped;
  const prevTotal = maybeNull(randInt(4, 20), 0.7);
  return {
    total,
    by_level: RISK_LEVELS.map((level) => ({ level, count: randInt(0, 10) })),
    mapped_count: mapped,
    unmapped_count: unmapped,
    prev_total: prevTotal,
    delta: prevTotal != null ? total - prevTotal : null,
  };
};

const genScopeSystems = (): ScopeSystemsResponse => {
  const systems = pickMany(SYSTEMS, randInt(2, 4)).map(({ system_code, name }) => ({
    id: system_code,
    name,
    category: maybeNull(pick(['erp', 'payroll', 'crm', 'treasury'])),
  }));
  return { total: systems.length, systems };
};

const genScopeEntities = (): ScopeEntitiesResponse => {
  const entities = makeRows(3, 5, () => ({
    id: shortId('ent'),
    name: `${pick(DEPARTMENTS)} ${pick(['LLC', 'Ltd', 'Division', 'Holdings'])}`,
    entity_type: maybeNull(pick(['legal_entity', 'business_unit'])),
  }));
  return { total: entities.length, entities };
};

const genScopeMateriality = (): ScopeMaterialityResponse => ({
  assessments: makeRows(1, 2, () => ({
    id: shortId('mat'),
    cycle_name: maybeNull(pick(CYCLES)),
    overall_materiality: randInt(50000, 500000),
    benchmark: maybeNull(pick(['Revenue', 'Net Income', 'Total Assets'])),
    status: maybeNull(pick(['draft', 'accepted', 'in_review'])),
    updated_at: isoDate(-60, -1),
  })),
});

const genScopePlannedCompleted = (): ScopePlannedCompletedResponse => {
  const planned = randInt(20, 80);
  const completed = randInt(0, planned);
  return { planned, completed, completion_pct: Number(((completed / planned) * 100).toFixed(1)) };
};

const genScopeOverdueSummary = (): ScopeOverdueSummaryResponse => ({
  testing_overdue: randInt(0, 8),
  pbc_overdue: randInt(0, 5),
  testing: makeRows(0, 4, () => ({
    test_run_id: shortId('run'),
    control_id: randomControlId(),
    control_title: randomTitle(),
    assignee_name: maybeNull(pick(PEOPLE)),
    due_date: isoDate(-20, -1),
    days_overdue: randInt(1, 30),
  })),
  pbc: makeRows(0, 3, () => ({
    id: shortId('pbc'),
    title: `PBC — ${pick(CONTROL_TITLES)}`,
    control_id: randomControlId(),
    assigned_to_name: maybeNull(pick(PEOPLE)),
    due_date: isoDate(-15, -1),
    days_overdue: randInt(1, 20),
  })),
});

const genScopeTestingStatusRollup = (): ScopeTestingStatusRollupResponse => {
  const byStatus = ['draft', 'in_progress', 'completed', 'reviewed'].map((status) => ({
    status,
    count: randInt(0, 25),
  }));
  return { total: byStatus.reduce((sum, s) => sum + s.count, 0), by_status: byStatus };
};

const genTesterAvgTestTime = (): TesterAvgTestTimeResponse => ({
  overall_avg_days: randFloat(1, 12, 1),
  per_tester: pickMany(PEOPLE, randInt(3, 5)).map((tester_name) => ({
    assignee_user_id: randInt(100, 999),
    tester_name,
    avg_days: randFloat(1, 14, 1),
    tests_completed: randInt(1, 20),
  })),
});

const genReviewerQueue = (): ReviewerQueueResponse => {
  const items = makeRows(3, 6, () => ({
    test_run_id: shortId('run'),
    control_id: randomControlId(),
    control_title: randomTitle(),
    tester_name: maybeNull(pick(PEOPLE)),
    workflow_status: pick(['complete', 'completed']),
    completed_at: isoDate(-14, -1),
    days_waiting: randInt(0, 14),
  }));
  return { total: items.length, items };
};

const genReviewerSummary = (): ReviewerSummaryResponse => ({
  pending_review: randInt(2, 20),
  reviewed_count: randInt(5, 40),
  avg_days_waiting: randFloat(0.5, 8, 1),
  by_tester: pickMany(PEOPLE, randInt(3, 5)).map((tester_name) => ({ tester_name, pending: randInt(0, 8) })),
});

const genAuditorProcessCharting = (): AuditorProcessChartingStatusResponse => {
  const byCycle = pickMany(CYCLES, randInt(3, 5)).map((cycle_name) => {
    const total = randInt(3, 12);
    return { cycle_name, total, charted: randInt(0, total) };
  });
  return {
    total_processes: byCycle.reduce((sum, c) => sum + c.total, 0),
    charted: byCycle.reduce((sum, c) => sum + c.charted, 0),
    uncharted: byCycle.reduce((sum, c) => sum + (c.total - c.charted), 0),
    by_cycle: byCycle,
  };
};

// ---------------------------------------------------------------------------
// widget_key -> { queryKey, getData } registry consumed by `WidgetPreview`
// ---------------------------------------------------------------------------

export type PreviewSeed = {
  /** The exact React Query key the widget's own hook reads from. */
  queryKey: readonly unknown[];
  /** Generates fresh sample data shaped like the real fetch response — call
   * again (e.g. on every preview mount) to rotate the values. */
  getData: () => unknown;
};

/**
 * `tester_evidence_checklist` and `auditor_workpapers_package` are
 * intentionally excluded: both widgets are disabled (`enabled: !!id`) until
 * a user pastes a run/control id into their own picker input, so they never
 * fire a network request in the gallery preview either — no seed needed,
 * they just render their built-in "select a run/control" placeholder, same
 * as today.
 */
export const PREVIEW_SEEDS: Record<string, PreviewSeed> = {
  // Controller
  controller_test_summary: { queryKey: analyticsControllerQueryKeys.testSummary(), getData: genControllerTestSummary },
  controller_failing_controls: {
    queryKey: analyticsControllerQueryKeys.failingControls(),
    getData: genControllerFailingControls,
  },
  controller_exception_rate: {
    queryKey: analyticsControllerQueryKeys.exceptionRate(),
    getData: genControllerExceptionRate,
  },
  controller_sample_size: { queryKey: analyticsControllerQueryKeys.sampleSize(), getData: genControllerSampleSize },
  controller_rollforward: { queryKey: analyticsControllerQueryKeys.rollforward(), getData: genControllerRollforward },
  controller_ipe_status: { queryKey: analyticsControllerQueryKeys.ipeStatus(), getData: genControllerIPEStatus },
  controller_tod_toe: { queryKey: analyticsControllerQueryKeys.todToe(), getData: genControllerToDToE },
  controller_sod_verified: {
    queryKey: analyticsControllerQueryKeys.sodVerified(),
    getData: genControllerSoDVerified,
  },
  controller_evidence_coverage: {
    queryKey: analyticsControllerQueryKeys.evidenceCoverage(),
    getData: genControllerEvidenceCoverage,
  },

  // CAE / Manager
  cae_control_universe: { queryKey: analyticsCaeQueryKeys.controlUniverse(), getData: genCaeControlUniverse },
  cae_testing_status: { queryKey: analyticsCaeQueryKeys.testingStatus(), getData: genCaeTestingStatus },
  cae_issues_pipeline: { queryKey: analyticsCaeQueryKeys.issuesPipeline(), getData: genCaeIssuesPipeline },
  cae_coverage_matrix: { queryKey: analyticsCaeQueryKeys.coverageMatrix(), getData: genCaeCoverageMatrix },
  cae_mrc: { queryKey: analyticsCaeQueryKeys.mrc(), getData: genCaeMRC },
  cae_exceptions_summary: { queryKey: analyticsCaeQueryKeys.exceptionsSummary(), getData: genCaeExceptionsSummary },
  cae_auditor_reliance: { queryKey: analyticsCaeQueryKeys.auditorReliance(), getData: genCaeAuditorReliance },
  cae_action_plan_aging: { queryKey: analyticsCaeQueryKeys.actionPlanAging(), getData: genCaeActionPlanAging },
  cae_controls_by_dept: { queryKey: analyticsCaeQueryKeys.controlsByDept(), getData: genCaeControlsByDept },
  cae_itgc_coverage: { queryKey: analyticsCaeQueryKeys.itgcCoverage(), getData: genCaeITGCCoverage },
  cae_tester_workload: { queryKey: analyticsCaeQueryKeys.testerWorkload(), getData: genCaeTesterWorkload },

  // CFO / Executive
  cfo_compliance_score: { queryKey: analyticsCfoQueryKeys.complianceScore(), getData: genCfoComplianceScore },
  cfo_coverage_heatmap: { queryKey: analyticsCfoQueryKeys.coverageHeatmap(), getData: genCfoCoverageHeatmap },
  cfo_open_issues: { queryKey: analyticsCfoQueryKeys.openIssues(), getData: genCfoOpenIssues },
  cfo_program_progress: { queryKey: analyticsCfoQueryKeys.programProgress(), getData: genCfoProgramProgress },
  cfo_soc_coverage: { queryKey: analyticsCfoQueryKeys.socCoverage(), getData: genCfoSOCCoverage },
  cfo_risk_landscape: { queryKey: analyticsCfoQueryKeys.riskLandscape(), getData: genCfoRiskLandscape },
  cfo_repeat_escalated: { queryKey: analyticsCfoQueryKeys.repeatEscalated(), getData: genCfoRepeatEscalated },
  cfo_score_trend: { queryKey: analyticsCfoQueryKeys.scoreTrend(), getData: genCfoScoreTrend },

  // Tester "my work"
  tester_my_controls: { queryKey: analyticsTesterQueryKeys.myControls(), getData: genTesterMyControls },
  tester_due_this_week: { queryKey: analyticsTesterQueryKeys.dueThisWeek(), getData: genTesterDueThisWeek },
  tester_my_progress: { queryKey: analyticsTesterQueryKeys.myProgress(), getData: genTesterMyProgress },
  tester_my_exceptions: { queryKey: analyticsTesterQueryKeys.myExceptions(), getData: genTesterMyExceptions },
  tester_reruns_required: { queryKey: analyticsTesterQueryKeys.rerunsRequired(), getData: genTesterRerunsRequired },

  // External Auditor
  auditor_control_inventory: {
    queryKey: analyticsAuditorQueryKeys.controlInventory(),
    getData: genAuditorControlInventory,
  },
  auditor_exception_report: {
    queryKey: analyticsAuditorQueryKeys.exceptionReport(),
    getData: genAuditorExceptionReport,
  },
  auditor_issue_ledger: { queryKey: analyticsAuditorQueryKeys.issueLedger(), getData: genAuditorIssueLedger },
  auditor_reliance_eligible: {
    queryKey: analyticsAuditorQueryKeys.relianceEligible(),
    getData: genAuditorRelianceEligible,
  },
  auditor_process_charting: {
    queryKey: analyticsAuditorQueryKeys.processChartingStatus(),
    getData: genAuditorProcessCharting,
  },

  // Scope & Coverage (feedback round 2026-08) — several widget keys share the
  // same underlying `/api/analytics/scope/*` query (e.g. control_counts is
  // also read by the key/non-key donut and the control-count delta tile);
  // each preview gets its own isolated QueryClient (see `WidgetPreview`), so
  // seeding the same key from multiple entries is safe.
  scope_control_counts: { queryKey: analyticsScopeQueryKeys.controlCounts(), getData: genScopeControlCounts },
  scope_key_nonkey: { queryKey: analyticsScopeQueryKeys.controlCounts(), getData: genScopeControlCounts },
  scope_controls_by_risk: { queryKey: analyticsScopeQueryKeys.controlCounts(), getData: genScopeControlCounts },
  scope_control_delta: { queryKey: analyticsScopeQueryKeys.controlCounts(), getData: genScopeControlCounts },
  scope_risk_summary: { queryKey: analyticsScopeQueryKeys.riskSummary(), getData: genScopeRiskSummary },
  scope_risks_mapped: { queryKey: analyticsScopeQueryKeys.riskSummary(), getData: genScopeRiskSummary },
  scope_risk_delta: { queryKey: analyticsScopeQueryKeys.riskSummary(), getData: genScopeRiskSummary },
  scope_systems: { queryKey: analyticsScopeQueryKeys.systems(), getData: genScopeSystems },
  scope_entities: { queryKey: analyticsScopeQueryKeys.entities(), getData: genScopeEntities },
  scope_materiality: { queryKey: analyticsScopeQueryKeys.materiality(), getData: genScopeMateriality },
  scope_planned_completed: {
    queryKey: analyticsScopeQueryKeys.plannedCompleted(),
    getData: genScopePlannedCompleted,
  },
  scope_overdue: { queryKey: analyticsScopeQueryKeys.overdueSummary(), getData: genScopeOverdueSummary },
  scope_testing_status: {
    queryKey: analyticsScopeQueryKeys.testingStatusRollup(),
    getData: genScopeTestingStatusRollup,
  },

  // Tester avg-time (feedback round 2026-08)
  tester_avg_time: { queryKey: analyticsTesterQueryKeys.avgTestTime(), getData: genTesterAvgTestTime },

  // Reviewer (6th persona, feedback round 2026-08)
  reviewer_queue: { queryKey: analyticsReviewerQueryKeys.queue(), getData: genReviewerQueue },
  reviewer_summary: { queryKey: analyticsReviewerQueryKeys.summary(), getData: genReviewerSummary },
  reviewer_pending_by_tester: { queryKey: analyticsReviewerQueryKeys.summary(), getData: genReviewerSummary },
};

/** Looks up the preview seed for a gallery widget, or `undefined` for the two
 * param-gated widgets that fall back to their existing empty-state behavior. */
export function getPreviewSeed(widgetKey: string): PreviewSeed | undefined {
  return PREVIEW_SEEDS[widgetKey];
}
